/**
 * Asks the rest of the app to finish saving before the session goes.
 *
 * CONTRACT §7: `mcw:before-sign-out` carries `waitUntil(p)`; whoever still
 * holds unsent changes hands over a promise that resolves true once they are
 * on the server. Resolves false if any of them says false, throws, or is still
 * going after three seconds, so the caller can ask before logging out anyway.
 * With no one listening, there is nothing to wait for.
 */

const WAIT_MS = 3000;

export interface BeforeSignOutDetail {
  waitUntil: (promise: Promise<boolean>) => void;
}

export async function flushBeforeSignOut(): Promise<boolean> {
  const pending: Promise<boolean>[] = [];
  const detail: BeforeSignOutDetail = { waitUntil: (promise) => pending.push(promise) };
  window.dispatchEvent(new CustomEvent<BeforeSignOutDetail>('mcw:before-sign-out', { detail }));
  if (pending.length === 0) return true;

  const settled = Promise.all(pending.map((p) => p.then((ok) => ok === true, () => false))).then((all) =>
    all.every(Boolean),
  );
  const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), WAIT_MS));
  return Promise.race([settled, timeout]);
}
